/**
 * The geometry of a voiceprint's mark: rings of arcs around a small core, drawn from the
 * voiceprint's id and from nothing else. The same id always gives the same mark; two ids give
 * marks that look nothing alike however similar the two voices are. It is a handle for the eye
 * ("this voiceprint again"), never evidence, and it reads nothing of the audio. Pure.
 *
 * Each of the three rings takes one of three characters — one long sweep, a few arcs of mixed
 * lengths, or a row of even dashes — and no two rings of a mark share one. One ring is drawn
 * heavier than the others. Which ring is which, where each starts and how long its arcs are
 * come from the id. At 20 px the inner ring is left out and the strokes are thicker, so the
 * mark stays crisp: the two outer rings keep their arcs.
 */

/** The side of the square the mark is drawn in; every length below is in these units. */
export const GLYPH_BOX = 64

/** The sizes the mark is drawn at, in px. */
export const GLYPH_SIZES = [20, 28, 40, 64] as const

export type GlyphSize = (typeof GLYPH_SIZES)[number]

/** One arc of a ring, in degrees clockwise from twelve o'clock. */
export interface GlyphArc {
  start: number
  sweep: number
}

export interface GlyphRing {
  radius: number
  /** Stroke width. */
  weight: number
  arcs: readonly GlyphArc[]
}

export interface GlyphGeometry {
  /** The outer ring first; two rings at 20 px, three above it. */
  rings: readonly GlyphRing[]
  /** Radius of the dot in the middle. */
  core: number
  /** Stroke width of the plain circle that stands for the outer ring of a mark not in use. */
  outline: number
}

// ── What each size is drawn with ─────────────────────────────────────────────

interface SizeSpec {
  radii: readonly number[]
  light: number
  heavy: number
  core: number
  outline: number
}

/** Radii and strokes per size: thicker and fewer where there are fewer pixels. */
const SPEC: Record<GlyphSize, SizeSpec> = {
  20: { radii: [26.5, 15.5], light: 4.5, heavy: 8, core: 5, outline: 3.5 },
  28: { radii: [27.5, 18.5, 10], light: 3, heavy: 6.5, core: 3.5, outline: 2.5 },
  40: { radii: [28, 19.5, 11], light: 2.5, heavy: 5.5, core: 3.5, outline: 2 },
  64: { radii: [28.5, 20.5, 12], light: 2, heavy: 5, core: 3.25, outline: 1.5 },
}

// ── The character of a ring ──────────────────────────────────────────────────

type RingStyle = 'sweep' | 'mixed' | 'dashes'

/** Every way to give the three rings three different characters, outer ring first. */
const STYLE_ORDERS: readonly (readonly RingStyle[])[] = [
  ['sweep', 'mixed', 'dashes'],
  ['sweep', 'dashes', 'mixed'],
  ['mixed', 'sweep', 'dashes'],
  ['mixed', 'dashes', 'sweep'],
  ['dashes', 'sweep', 'mixed'],
  ['dashes', 'mixed', 'sweep'],
]

/** How many arcs a ring may have, [least, most], by character and by ring (outer first). */
const ARC_COUNTS: Record<RingStyle, readonly (readonly [number, number])[]> = {
  sweep: [
    [1, 2],
    [1, 2],
    [1, 1],
  ],
  mixed: [
    [2, 3],
    [2, 3],
    [2, 2],
  ],
  dashes: [
    [5, 6],
    [3, 4],
    [3, 3],
  ],
}

/** How unequal the arcs of a ring are: the longest may be this many times the shortest. */
const CONTRAST: Record<RingStyle, number> = { sweep: 3.5, mixed: 3, dashes: 1 }

/**
 * How much of the ring its arcs cover, [least, most]: a sweep leaves a wide opening, and where
 * that opening points is what tells two marks apart at a glance. It always covers more than
 * half the ring, so it reads as an open ring and never as a bowl.
 */
const COVER: Record<RingStyle, readonly [number, number]> = {
  sweep: [0.58, 0.8],
  mixed: [0.86, 1],
  dashes: [1, 1],
}

/**
 * The inner ring's sweep closes further: one arc around one dot with a wide opening upward would
 * read as a face, and the mark must read as nothing but a mark.
 */
const INNER_SWEEP_COVER: readonly [number, number] = [0.8, 0.93]

/** The least gap left open between two arcs after their round caps, in box units. */
const MIN_OPEN_GAP = 3

/** No arc is shorter than this many degrees: under it a round-capped arc is a dot. */
const MIN_SWEEP = 14

// ── A number sequence from an id ─────────────────────────────────────────────

/** FNV-1a of the id: a 32-bit seed. */
function seedOf(id: string): number {
  let hash = 2166136261
  for (let index = 0; index < id.length; index++) {
    hash ^= id.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/** Mulberry32: numbers in [0, 1) that depend on the seed alone. */
function sequence(seed: number): () => number {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let mixed = Math.imul(state ^ (state >>> 15), 1 | state)
    mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296
  }
}

const pick = (next: () => number, least: number, most: number): number =>
  least + Math.floor(next() * (most - least + 1))

const round = (value: number): number => Math.round(value * 100) / 100

// ── The plan of a mark: what the id decides, whatever the size ───────────────

interface RingPlan {
  /** Where the first arc starts, in degrees. */
  turn: number
  /** The arcs' shares of what the ring covers, in drawing order: they add up to 1. */
  shares: readonly number[]
  /** How much of the ring the arcs cover, 0..1; the rest is one opening after the last arc. */
  cover: number
}

interface GlyphPlan {
  rings: readonly RingPlan[]
  /** Which ring is drawn heavier, outer ring first. */
  heavy: number
}

function planRing(next: () => number, style: RingStyle, ring: number): RingPlan {
  const [fewest, mostArcs] = ARC_COUNTS[style][ring]
  const count = pick(next, fewest, mostArcs)
  const lengths = Array.from({ length: count }, () => 1 + next() * (CONTRAST[style] - 1))
  const total = lengths.reduce((sum, length) => sum + length, 0)
  const [least, most] = style === 'sweep' && ring === 2 ? INNER_SWEEP_COVER : COVER[style]
  return {
    turn: next() * 360,
    shares: lengths.map(length => length / total),
    cover: least + next() * (most - least),
  }
}

/** What the id decides. The numbers are drawn in a fixed order, so every size reads the same. */
function planOf(id: string): GlyphPlan {
  const next = sequence(seedOf(id))
  const styles = STYLE_ORDERS[pick(next, 0, STYLE_ORDERS.length - 1)]
  const heavy = pick(next, 0, 2)
  return { rings: styles.map((style, ring) => planRing(next, style, ring)), heavy }
}

// ── The plan at a size ───────────────────────────────────────────────────────

/** The gap between two arcs in degrees: room for both round caps and some air between them. */
function gapDegrees(radius: number, weight: number): number {
  const open = Math.max(MIN_OPEN_GAP, weight * 0.9)
  return ((weight + open) / radius) * (180 / Math.PI)
}

/**
 * The arcs of a ring from its shares: every arc at least `MIN_SWEEP` long, the rest of what the
 * ring covers handed out by share, a gap after each.
 */
function arcsOf(plan: RingPlan, radius: number, weight: number): GlyphArc[] {
  const gap = gapDegrees(radius, weight)
  const count = plan.shares.length
  const free = (360 - count * (gap + MIN_SWEEP)) * plan.cover
  let start = plan.turn
  return plan.shares.map(share => {
    const sweep = MIN_SWEEP + Math.max(0, free) * share
    const arc = { start: round(start % 360), sweep: round(sweep) }
    start += sweep + gap
    return arc
  })
}

/** The mark of a voiceprint at a size. Deterministic: the id is the only input that varies it. */
export function voiceGlyph(id: string, size: GlyphSize = 40): GlyphGeometry {
  const spec = SPEC[size]
  const plan = planOf(id)
  const rings = spec.radii.map((radius, ring) => {
    const weight = ring === plan.heavy ? spec.heavy : spec.light
    return { radius, weight, arcs: arcsOf(plan.rings[ring], radius, weight) }
  })
  return { rings, core: spec.core, outline: spec.outline }
}

// ── Drawing ──────────────────────────────────────────────────────────────────

const CENTRE = GLYPH_BOX / 2

/** A point on a ring, `degrees` clockwise from twelve o'clock. */
function pointAt(radius: number, degrees: number): string {
  const angle = (degrees * Math.PI) / 180
  return `${round(CENTRE + radius * Math.sin(angle))} ${round(CENTRE - radius * Math.cos(angle))}`
}

/** The SVG path of one arc of a ring. */
export function arcPath(radius: number, arc: GlyphArc): string {
  const large = arc.sweep > 180 ? 1 : 0
  const from = pointAt(radius, arc.start)
  const to = pointAt(radius, arc.start + arc.sweep)
  return `M${from}A${radius} ${radius} 0 ${large} 1 ${to}`
}

/** The first four hex characters of a voiceprint id: what is written in mono beside its mark. */
export function voiceprintShortId(id: string): string {
  return id
    .replace(/[^0-9a-f]/gi, '')
    .slice(0, 4)
    .toLowerCase()
}
